import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../../services/api';
import { useWorkspace } from '../../WorkspaceContext';

const FALLBACK_NETWORKS = [
  { code: 'MTN', label: 'MTN MoMo' },
  { code: 'VODAFONE', label: 'Telecel / Vodafone Cash' },
  { code: 'AIRTELTIGO', label: 'AirtelTigo Money' },
];

const DEFAULT_PLATFORM_FEE_PERCENT = 2;

const WALLET_CODES = new Set(['MTN', 'VODAFONE', 'AIRTELTIGO', 'MPS']);

const emptyPayoutDraft = {
  method: 'mobile_money',
  network: 'MTN',
  accountBank: '',
  accountBankName: '',
  accountNumber: '',
  accountName: '',
};

const defaultPaymentDraft = (location) => ({
  paymentCollection: location?.paymentCollection === 'manual' ? 'manual' : 'online_deposit',
  depositRefundable: location?.depositRefundable !== false,
  depositType: location?.depositType === 'fixed' ? 'fixed' : 'percent',
  depositValue:
    location?.depositValue !== undefined && location?.depositValue !== null
      ? Number(location.depositValue)
      : location?.depositType === 'fixed'
        ? 0
        : 25,
  depositMinAmount:
    location?.depositMinAmount !== undefined && location?.depositMinAmount !== null
      ? Number(location.depositMinAmount)
      : 50,
});

const methodFromPayout = (data) => {
  if (data?.payoutMethod === 'bank') return 'bank';
  if (data?.payoutMethod === 'mobile_money' || data?.payoutMethod === 'mpesa') {
    return 'mobile_money';
  }
  if (data?.payoutAccountBank && !WALLET_CODES.has(data.payoutAccountBank)) return 'bank';
  if (data?.suggestedMethod === 'bank') return 'bank';
  return 'mobile_money';
};

const fieldClass =
  'w-full min-w-0 bg-white px-3 py-3 text-base text-ink-900 ring-1 ring-inset ring-ink-900/15 focus:outline-none focus:ring-2 focus:ring-saffron-500 sm:px-4 sm:text-sm';
const chipClass = (active) =>
  `min-h-11 w-full px-3 py-2.5 text-sm leading-snug touch-manipulation ${
    active ? 'bg-ink-900 text-sand-50' : 'bg-white text-ink-800 ring-1 ring-ink-900/10'
  }`;

const ManagePayments = () => {
  const { activeLocation, refresh } = useWorkspace();
  const [error, setError] = useState('');
  const [paymentDraft, setPaymentDraft] = useState(() => defaultPaymentDraft(null));
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentSaved, setPaymentSaved] = useState(false);

  const [payoutInfo, setPayoutInfo] = useState(null);
  const [payoutDraft, setPayoutDraft] = useState(emptyPayoutDraft);
  const [banks, setBanks] = useState([]);
  const [savingPayout, setSavingPayout] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [payoutSaved, setPayoutSaved] = useState(false);

  const country = payoutInfo?.suggestedCountry || payoutInfo?.payoutCountry || 'GH';
  const networks =
    payoutInfo?.networks?.length > 0 ? payoutInfo.networks : FALLBACK_NETWORKS;
  const numberLabel = country === 'KE' ? 'M-Pesa number' : 'Mobile money number';
  const connectedNumber =
    payoutInfo?.payoutAccountNumber || payoutInfo?.payoutAccountNumberMasked;
  const connectedIsBank =
    payoutInfo?.payoutMethod === 'bank' ||
    (payoutInfo?.payoutAccountBank && !WALLET_CODES.has(payoutInfo.payoutAccountBank));
  const payoutReady = Boolean(payoutInfo?.payoutReady);
  const platformFeePercent =
    payoutInfo?.platformFeePercent > 0
      ? payoutInfo.platformFeePercent
      : DEFAULT_PLATFORM_FEE_PERCENT;

  useEffect(() => {
    setPaymentDraft(defaultPaymentDraft(activeLocation));
    setPaymentSaved(false);
    setError('');
  }, [activeLocation?.id, activeLocation]);

  useEffect(() => {
    if (!activeLocation?.id) return;
    let cancelled = false;
    (async () => {
      const result = await api.getWorkspacePayout(activeLocation.id);
      if (cancelled || result.error) return;
      const data = result.data;
      setPayoutInfo(data);
      setPayoutDraft({
        method: methodFromPayout(data),
        network: WALLET_CODES.has(data?.payoutAccountBank)
          ? data.payoutAccountBank
          : data?.networks?.[0]?.code || 'MTN',
        accountBank: WALLET_CODES.has(data?.payoutAccountBank) ? '' : data?.payoutAccountBank || '',
        accountBankName: WALLET_CODES.has(data?.payoutAccountBank)
          ? ''
          : data?.payoutAccountBankName || '',
        accountNumber: data?.payoutAccountNumber || '',
        accountName: data?.payoutAccountName || '',
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [activeLocation?.id]);

  useEffect(() => {
    if (!activeLocation?.id || payoutDraft.method !== 'bank') return;
    let cancelled = false;
    (async () => {
      const result = await api.getWorkspacePayoutBanks(country, activeLocation.id);
      if (cancelled || result.error) return;
      setBanks(result.data?.banks || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeLocation?.id, country, payoutDraft.method]);

  const paymentDirty = useMemo(() => {
    const current = defaultPaymentDraft(activeLocation);
    return (
      paymentDraft.paymentCollection !== current.paymentCollection ||
      paymentDraft.depositRefundable !== current.depositRefundable ||
      paymentDraft.depositType !== current.depositType ||
      Number(paymentDraft.depositValue) !== Number(current.depositValue) ||
      Number(paymentDraft.depositMinAmount) !== Number(current.depositMinAmount)
    );
  }, [paymentDraft, activeLocation]);

  const savePayment = async (event) => {
    event.preventDefault();
    if (!activeLocation || !paymentDirty) return;

    setSavingPayment(true);
    setError('');
    setPaymentSaved(false);
    const result = await api.updateWorkspaceLocation(activeLocation.id, {
      paymentCollection: paymentDraft.paymentCollection,
      depositRefundable: paymentDraft.depositRefundable,
      depositType: paymentDraft.depositType,
      depositValue: Number(paymentDraft.depositValue),
      depositMinAmount: Number(paymentDraft.depositMinAmount),
    });
    setSavingPayment(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setPaymentSaved(true);
    await refresh();
  };

  const resolveBank = async () => {
    if (!payoutDraft.accountBank || !payoutDraft.accountNumber) return;
    setResolving(true);
    setError('');
    const result = await api.resolveWorkspacePayoutAccount({
      accountBank: payoutDraft.accountBank,
      accountNumber: payoutDraft.accountNumber,
      locationId: activeLocation?.id,
    });
    setResolving(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    if (result.data?.accountName) {
      setPayoutDraft((prev) => ({ ...prev, accountName: result.data.accountName }));
    }
  };

  const savePayout = async (event) => {
    event.preventDefault();
    if (!activeLocation) return;
    setSavingPayout(true);
    setError('');
    setPayoutSaved(false);

    const payload = {
      locationId: activeLocation.id,
      country,
      method: payoutDraft.method === 'bank' ? 'bank' : 'mobile_money',
      businessMobile: payoutDraft.accountNumber || activeLocation.phone,
      accountNumber: payoutDraft.accountNumber,
      accountName: payoutDraft.accountName,
    };

    if (payoutDraft.method === 'bank') {
      payload.accountBank = payoutDraft.accountBank;
      payload.accountBankName = payoutDraft.accountBankName;
    } else {
      payload.network = payoutDraft.network;
    }

    const result = await api.saveWorkspacePayout(payload);
    setSavingPayout(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    setPayoutInfo((prev) => ({ ...prev, ...result.data }));
    setPayoutDraft((prev) => ({
      ...prev,
      accountNumber: result.data?.payoutAccountNumber || prev.accountNumber,
      accountName: result.data?.payoutAccountName || prev.accountName,
    }));
    setPayoutSaved(true);
    await refresh();
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {error && (
        <div className="break-words border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <section className="bg-sand-50 p-4 ring-1 ring-ink-900/10 sm:p-8">
        <p className="text-[11px] uppercase tracking-[0.18em] text-saffron-600 sm:text-xs sm:tracking-[0.22em]">
          Payments
        </p>
        <h2 className="mt-2 font-display text-2xl font-light text-ink-900 sm:text-3xl">
          How clients pay
        </h2>
        <p className="mt-2 max-w-xl text-sm text-ink-700/80">
          Choose whether clients pay a deposit to book, or book free and pay at the salon. Platform
          fees to process online payments are {platformFeePercent}%. After about 24 hours, send
          cleared payouts from Sales.
        </p>

        {paymentDraft.paymentCollection === 'online_deposit' && !payoutReady && (
          <p className="mt-4 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Online deposits need a payout wallet or bank account. Save one in the section below
            before clients can pay.
          </p>
        )}

        <form onSubmit={savePayment} className="mt-6 space-y-6">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
              Collection mode
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                {
                  id: 'online_deposit',
                  label: 'Collect deposit online',
                  hint: `Client pays via mobile money or card. Platform fees to process online payments are ${platformFeePercent}%. After about 24 hours, send cleared payouts from Sales.`,
                },
                {
                  id: 'manual',
                  label: 'No deposit — pay at salon (cash/MoMo)',
                  hint: 'Bookings confirm with no online payment. Collect when they arrive.',
                },
              ].map((option) => {
                const selected = paymentDraft.paymentCollection === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() =>
                      setPaymentDraft((prev) => ({ ...prev, paymentCollection: option.id }))
                    }
                    className={`px-4 py-4 text-left transition-colors ${
                      selected
                        ? 'bg-ink-900 text-sand-50'
                        : 'bg-white text-ink-900 ring-1 ring-ink-900/10 hover:bg-white/80'
                    }`}
                  >
                    <span className="block text-sm font-semibold">{option.label}</span>
                    <span
                      className={`mt-1 block text-sm ${
                        selected ? 'text-sand-300' : 'text-ink-600/70'
                      }`}
                    >
                      {option.hint}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {paymentDraft.paymentCollection === 'online_deposit' && (
            <>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Deposit amount
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'percent', label: 'Percent of service' },
                    { id: 'fixed', label: 'Fixed amount' },
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() =>
                        setPaymentDraft((prev) => ({ ...prev, depositType: option.id }))
                      }
                      className={`min-h-11 px-4 py-2.5 text-sm ${
                        paymentDraft.depositType === option.id
                          ? 'bg-ink-900 text-sand-50'
                          : 'bg-white text-ink-800 ring-1 ring-ink-900/10'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                      {paymentDraft.depositType === 'percent' ? 'Percent' : 'Amount'}
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={paymentDraft.depositType === 'percent' ? 100 : undefined}
                      step={1}
                      value={paymentDraft.depositValue}
                      onChange={(e) =>
                        setPaymentDraft((prev) => ({
                          ...prev,
                          depositValue: e.target.value,
                        }))
                      }
                      className={fieldClass}
                    />
                  </div>
                  {paymentDraft.depositType === 'percent' && (
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                        Minimum deposit
                      </label>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={paymentDraft.depositMinAmount}
                        onChange={(e) =>
                          setPaymentDraft((prev) => ({
                            ...prev,
                            depositMinAmount: e.target.value,
                          }))
                        }
                        className={fieldClass}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
                  Refund policy
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    {
                      id: true,
                      label: 'Refundable',
                      hint: 'You can refund the deposit later (e.g. cancellations).',
                    },
                    {
                      id: false,
                      label: 'Non-refundable',
                      hint: 'Shown to clients; keep the deposit if they no-show.',
                    },
                  ].map((option) => {
                    const selected = paymentDraft.depositRefundable === option.id;
                    return (
                      <button
                        key={String(option.id)}
                        type="button"
                        onClick={() =>
                          setPaymentDraft((prev) => ({
                            ...prev,
                            depositRefundable: option.id,
                          }))
                        }
                        className={`px-4 py-4 text-left transition-colors ${
                          selected
                            ? 'bg-ink-900 text-sand-50'
                            : 'bg-white text-ink-900 ring-1 ring-ink-900/10'
                        }`}
                      >
                        <span className="block text-sm font-semibold">{option.label}</span>
                        <span
                          className={`mt-1 block text-sm ${
                            selected ? 'text-sand-300' : 'text-ink-600/70'
                          }`}
                        >
                          {option.hint}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
            <button
              type="submit"
              disabled={!paymentDirty || savingPayment || !activeLocation}
              className="min-h-11 w-full bg-saffron-500 px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
            >
              {savingPayment ? 'Saving…' : 'Save payment settings'}
            </button>
            {paymentSaved && !paymentDirty && (
              <span className="text-sm text-ink-600/70">Saved</span>
            )}
          </div>
        </form>
      </section>

      <section className="min-w-0 bg-sand-50 p-4 ring-1 ring-ink-900/10 sm:p-8">
        <p className="text-[11px] uppercase tracking-[0.18em] text-saffron-600 sm:text-xs sm:tracking-[0.22em]">
          Payouts
        </p>
        <h2 className="mt-2 font-display text-2xl font-light text-ink-900 sm:text-3xl">
          Where you get paid
        </h2>
        <p className="mt-2 max-w-xl text-sm text-ink-700/80">
          Save the mobile money wallet or bank account that should receive booking deposits.
          Platform fees to process online payments are {platformFeePercent}%. After about 24 hours,
          send cleared payouts from Sales.
        </p>

        {payoutReady && connectedNumber && (
          <div className="mt-4 min-w-0 bg-white px-3 py-3 text-sm text-ink-800 ring-1 ring-ink-900/10 sm:px-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-600/70">
              Connected {connectedIsBank ? 'bank account' : 'mobile money'}
            </p>
            <p className="mt-1 break-words font-medium text-ink-900">
              {payoutInfo.payoutAccountName || 'Account'}
            </p>
            <p className="mt-0.5 break-words">
              {payoutInfo.payoutAccountBankName || payoutInfo.payoutAccountBank}
            </p>
            <p className="mt-1 break-all font-mono text-base text-ink-900">{connectedNumber}</p>
          </div>
        )}

        <form onSubmit={savePayout} className="mt-6 space-y-4">
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-700 sm:text-xs sm:tracking-[0.14em]">
              Payout method
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'mobile_money', label: 'Mobile money' },
                { id: 'bank', label: 'Bank account' },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setPayoutDraft((prev) => ({ ...prev, method: option.id }))}
                  className={chipClass(payoutDraft.method === option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {payoutDraft.method === 'bank' ? (
            <>
              <div>
                <label
                  htmlFor="payout-bank"
                  className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-700 sm:text-xs sm:tracking-[0.14em]"
                >
                  Bank
                </label>
                <select
                  id="payout-bank"
                  value={payoutDraft.accountBank}
                  onChange={(e) => {
                    const code = e.target.value;
                    const bank = banks.find((item) => item.code === code);
                    setPayoutDraft((prev) => ({
                      ...prev,
                      accountBank: code,
                      accountBankName: bank?.name || '',
                    }));
                  }}
                  className={`${fieldClass} max-w-full`}
                  required
                >
                  <option value="">Select bank</option>
                  {banks.map((bank) => (
                    <option key={bank.code} value={bank.code}>
                      {bank.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="payout-account-number"
                  className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-700 sm:text-xs sm:tracking-[0.14em]"
                >
                  Account number
                </label>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    id="payout-account-number"
                    inputMode="numeric"
                    autoComplete="off"
                    value={payoutDraft.accountNumber}
                    onChange={(e) =>
                      setPayoutDraft((prev) => ({ ...prev, accountNumber: e.target.value }))
                    }
                    className={fieldClass}
                    required
                  />
                  <button
                    type="button"
                    onClick={resolveBank}
                    disabled={resolving || !payoutDraft.accountBank || !payoutDraft.accountNumber}
                    className="min-h-11 w-full touch-manipulation px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-ink-900/15 hover:bg-white disabled:opacity-50 sm:w-auto sm:flex-none"
                  >
                    {resolving ? 'Checking…' : 'Verify'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {networks.length > 1 && (
                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-700 sm:text-xs sm:tracking-[0.14em]">
                    Network
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {networks.map((network) => (
                      <button
                        key={network.code}
                        type="button"
                        onClick={() =>
                          setPayoutDraft((prev) => ({ ...prev, network: network.code }))
                        }
                        className={chipClass(payoutDraft.network === network.code)}
                      >
                        {network.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label
                  htmlFor="momo-number"
                  className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-700 sm:text-xs sm:tracking-[0.14em]"
                >
                  {numberLabel}
                </label>
                <input
                  id="momo-number"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={payoutDraft.accountNumber}
                  onChange={(e) =>
                    setPayoutDraft((prev) => ({ ...prev, accountNumber: e.target.value }))
                  }
                  className={fieldClass}
                  placeholder={country === 'KE' ? '07… or 254…' : '0… or 233…'}
                  required
                />
              </div>
            </>
          )}

          <div>
            <label
              htmlFor="payout-name"
              className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-700 sm:text-xs sm:tracking-[0.14em]"
            >
              Account name
            </label>
            <input
              id="payout-name"
              autoComplete="name"
              value={payoutDraft.accountName}
              onChange={(e) =>
                setPayoutDraft((prev) => ({ ...prev, accountName: e.target.value }))
              }
              className={fieldClass}
              placeholder={
                payoutDraft.method === 'bank' ? 'Name on the account' : 'Name on the wallet'
              }
            />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <button
              type="submit"
              disabled={savingPayout || !activeLocation}
              className="min-h-11 w-full touch-manipulation bg-saffron-500 px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
            >
              {savingPayout ? 'Saving…' : payoutReady ? 'Update payout' : 'Save payout'}
            </button>
            {payoutSaved && <span className="text-sm text-ink-600/70">Saved</span>}
          </div>
        </form>
      </section>
    </div>
  );
};

export default ManagePayments;
